import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import queryString from 'query-string';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Link from '@mui/material/Link';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoneIcon from '@mui/icons-material/Done';
import ErrorIcon from '@mui/icons-material/Error';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { ConnectorContext } from '../../context';
import { getOnboardingResult } from '../../fetch/requests';
import { keysToCamel, localizedUrl } from '../../utils';

export const RESULT_STATUSES = {
  CONNECTED: 'CONNECTED',
  WEBHOOK_LIVE: 'LIVE',
  WEBHOOK_PENDING: 'PENDING',
  WEBHOOK_FAILED: 'FAILED',
  TAGS_READY: 'READY',
  TAGS_PENDING: 'PENDING',
  TAGS_FAILED: 'FAILED',
};

// The webhook and the KYC tags are set up by flows that start after the
// onboarding, so the first answer is usually "pending". Ask again for up to a
// minute, until neither is pending.
export const POLL_INTERVAL = 3000;
export const MAX_POLL_ATTEMPTS = 20;

const STATUS_ICONS = {
  ok: <CheckCircleIcon color="success" data-testid="status_ok" />,
  failed: <ErrorIcon color="error" data-testid="status_failed" />,
  pending: <CircularProgress size={20} color="lightDark" data-testid="status_pending" />,
};

const getWebhookLine = (webhook, isPolling) => {
  if (webhook.status === RESULT_STATUSES.WEBHOOK_LIVE) {
    return { icon: 'ok', messageId: 'result_webhook_live' };
  }

  if (webhook.status === RESULT_STATUSES.WEBHOOK_FAILED) {
    return { icon: 'failed', messageId: 'result_webhook_failed' };
  }

  return isPolling
    ? { icon: 'pending', messageId: 'result_webhook_pending' }
    : { icon: 'failed', messageId: 'result_webhook_not_confirmed' };
};

const getTagsLine = (tags, isPolling) => {
  if (tags.status === RESULT_STATUSES.TAGS_READY) {
    return { icon: 'ok', messageId: 'result_tags_ready' };
  }

  if (tags.status === RESULT_STATUSES.TAGS_FAILED) {
    return { icon: 'failed', messageId: 'result_tags_failed' };
  }

  return isPolling
    ? { icon: 'pending', messageId: 'result_tags_pending' }
    : { icon: 'failed', messageId: 'result_tags_not_confirmed' };
};

const isPendingLine = (line) => line?.status === RESULT_STATUSES.WEBHOOK_PENDING;

// The logos of the connected systems, side by side above the title (Blikk and
// Verified for the KYC integration). A connection without a logo, or whose
// logo does not load, is left out, so the page never shows a broken image.
const ConnectedLogos = ({ connections, failed, onFailed }) => {
  const logos = connections.filter(
    (connection) => connection.applicationLogoUrl && !failed.includes(connection.applicationLogoUrl)
  );
  if (!logos.length) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '20px',
        width: '100%',
        paddingBottom: '20px',
      }}
      data-testid="result_logos"
    >
      {logos.map((connection, index) => (
        <React.Fragment key={connection.connectionId ?? connection.applicationLogoUrl}>
          {index > 0 ? (
            <LinkRoundedIcon sx={{ color: '#9e9e9e' }} data-testid="result_logos_link" />
          ) : null}
          <Box
            component="img"
            src={connection.applicationLogoUrl}
            alt={connection.title}
            onError={() => onFailed(connection.applicationLogoUrl)}
            sx={{ height: '64px', maxWidth: '140px', objectFit: 'contain' }}
            data-testid="result_logo"
          />
        </React.Fragment>
      ))}
    </Box>
  );
};

ConnectedLogos.propTypes = {
  connections: PropTypes.arrayOf(
    PropTypes.shape({
      connectionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      title: PropTypes.string,
      applicationLogoUrl: PropTypes.string,
    })
  ).isRequired,
  failed: PropTypes.arrayOf(PropTypes.string).isRequired,
  onFailed: PropTypes.func.isRequired,
};

// The button at the bottom. The application can send the customer on to where the
// next step happens (Blikk for the KYC integration); otherwise it goes to return_uri.
const FinishButton = ({ finish, returnUri }) => {
  const finishUrl = finish?.url || returnUri;
  if (!finishUrl) return null;

  return (
    <Grid item sx={{ paddingTop: '36px' }}>
      <Button
        variant="contained"
        color="blackBtn"
        sx={{ color: '#fff' }}
        href={finishUrl}
        fullWidth
        data-testid="result_finish_btn"
      >
        {finish?.url && finish?.label ? (
          <FormattedMessage id={finish.label} defaultMessage={finish.label} />
        ) : (
          <FormattedMessage id="result_finish_btn" />
        )}
      </Button>
    </Grid>
  );
};

FinishButton.propTypes = {
  finish: PropTypes.shape({ label: PropTypes.string, url: PropTypes.string }),
  returnUri: PropTypes.string,
};

FinishButton.defaultProps = {
  finish: undefined,
  returnUri: undefined,
};

// ---- The summary layout: a check, three plain lines and one sentence -----------
// An application asks for it with `layout: "summary"` in its result page. The lines
// are the connection to the first system, the KYC tags and the connection to the
// last one, which counts as set up when it works and its webhook is live; the
// customer is told what happened, not which parts the integration is made of.

const SUMMARY_TITLES = {
  ok: 'result_summary_title',
  pending: 'result_summary_title_pending',
  failed: 'result_summary_title_failed',
};

const SUMMARY_BADGES = {
  ok: { icon: <CheckCircleIcon color="success" sx={{ fontSize: 36 }} />, background: '#e8f5e9' },
  pending: { icon: <CircularProgress size={30} color="lightDark" />, background: '#f1f1f1' },
  failed: { icon: <ErrorIcon color="error" sx={{ fontSize: 36 }} />, background: '#fdecea' },
};

const SUMMARY_ICONS = {
  ok: <DoneIcon color="iconGrey" sx={{ fontSize: 18 }} data-testid="status_ok" />,
  failed: <ErrorIcon color="error" sx={{ fontSize: 18 }} data-testid="status_failed" />,
  pending: <CircularProgress size={16} color="lightDark" data-testid="status_pending" />,
};

const getWorkingLine = (connection) => {
  if (connection.status === RESULT_STATUSES.CONNECTED) {
    return { icon: 'ok', messageId: 'result_summary_working' };
  }

  return { icon: 'failed', messageId: 'result_summary_not_working' };
};

const getSetUpLine = (connection, webhook, isPolling) => {
  if (connection.status !== RESULT_STATUSES.CONNECTED) {
    return { icon: 'failed', messageId: 'result_summary_set_up_failed' };
  }

  if (!webhook || webhook.status === RESULT_STATUSES.WEBHOOK_LIVE) {
    return { icon: 'ok', messageId: 'result_summary_set_up' };
  }

  if (webhook.status === RESULT_STATUSES.WEBHOOK_FAILED) {
    return { icon: 'failed', messageId: 'result_summary_set_up_failed' };
  }

  return isPolling
    ? { icon: 'pending', messageId: 'result_summary_set_up_pending' }
    : { icon: 'failed', messageId: 'result_summary_set_up_not_confirmed' };
};

const getSummaryTagsLine = (tags, isPolling) => {
  const line = getTagsLine(tags, isPolling);

  return line.icon === 'ok' ? { ...line, messageId: 'result_summary_tags_ready' } : line;
};

const getSummaryLines = ({ connections, webhook, tags }, isPolling) => {
  const first = connections.length > 1 ? connections[0] : null;
  const last = connections[connections.length - 1];

  return [
    first && { key: 'first', appName: first.title, ...getWorkingLine(first) },
    tags && { key: 'tags', ...getSummaryTagsLine(tags, isPolling) },
    last && { key: 'last', appName: last.title, ...getSetUpLine(last, webhook, isPolling) },
  ].filter(Boolean);
};

// The guide is a link inside the sentence that names it: {guide} in the text.
const renderGuideLink = (guide, messages) => {
  if (!guide?.url || !guide?.label) return null;

  return (
    <Link
      href={localizedUrl(messages, guide.label, guide.url)}
      target="_blank"
      rel="noopener noreferrer"
      underline="always"
      sx={{ fontWeight: 700, color: 'inherit' }}
      data-testid="result_guide"
    >
      <FormattedMessage id={guide.label} defaultMessage={guide.label} />
    </Link>
  );
};

const SummaryResult = ({ result, isPolling, failedLogos, onLogoFailed }) => {
  const intl = useIntl();
  const { connections = [], instructions = [], returnUri, guide, finish } = result;
  const lines = getSummaryLines({ ...result, connections }, isPolling);
  let state = 'ok';
  if (lines.some((line) => line.icon === 'failed')) {
    state = 'failed';
  } else if (lines.some((line) => line.icon === 'pending')) {
    state = 'pending';
  }
  const badge = SUMMARY_BADGES[state];
  const guideLink = renderGuideLink(guide, intl.messages);
  // What the customer can do now is only said when everything is in place.
  const sentences = state === 'ok' ? instructions : [];

  return (
    <Grid container item justifyContent="center" direction="column" sx={{ margin: '0 auto' }}>
      <ConnectedLogos connections={connections} failed={failedLogos} onFailed={onLogoFailed} />

      <Box
        data-testid="result_badge"
        data-state={state}
        sx={{
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          backgroundColor: badge.background,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px',
        }}
      >
        {badge.icon}
      </Box>

      <Typography
        gutterBottom
        data-testid="result_title"
        sx={{ textAlign: 'center', color: '#484848', fontSize: '18px', fontWeight: 700 }}
      >
        <FormattedMessage id={SUMMARY_TITLES[state]} />
      </Typography>

      <Box sx={{ padding: '16px 0 8px' }} data-testid="result_statuses">
        {lines.map((line) => (
          <Grid container item alignItems="center" key={line.key} sx={{ paddingBottom: '10px' }}>
            <Box sx={{ display: 'flex', width: '18px', marginRight: '15px' }}>
              {SUMMARY_ICONS[line.icon]}
            </Box>
            <Typography variant="normal" sx={{ flex: 1 }} data-testid={`result_line_${line.key}`}>
              <FormattedMessage id={line.messageId} values={{ appName: line.appName }} />
            </Typography>
          </Grid>
        ))}
      </Box>

      {sentences.map((instruction) => (
        <Typography
          key={instruction}
          variant="body2"
          sx={{ padding: '12px 0 0', color: 'text.secondary' }}
          data-testid="result_summary_text"
        >
          <FormattedMessage
            id={instruction}
            defaultMessage={instruction}
            values={{ guide: guideLink }}
          />
        </Typography>
      ))}

      <FinishButton finish={finish} returnUri={returnUri} />
    </Grid>
  );
};

SummaryResult.propTypes = {
  result: PropTypes.shape({
    connections: PropTypes.array,
    instructions: PropTypes.arrayOf(PropTypes.string),
    returnUri: PropTypes.string,
    guide: PropTypes.shape({ label: PropTypes.string, url: PropTypes.string }),
    finish: PropTypes.shape({ label: PropTypes.string, url: PropTypes.string }),
  }).isRequired,
  isPolling: PropTypes.bool.isRequired,
  failedLogos: PropTypes.arrayOf(PropTypes.string).isRequired,
  onLogoFailed: PropTypes.func.isRequired,
};

export const OnboardingResult = () => {
  const {
    state: { onboardingResult, error },
    updateState,
  } = useContext(ConnectorContext);
  const intl = useIntl();
  const { search } = useLocation();
  const { clientId, state } = keysToCamel(queryString.parse(search));
  const [attempt, setAttempt] = useState(0);
  const [failedLogos, setFailedLogos] = useState([]);

  const isAnyLinePending = [onboardingResult?.webhook, onboardingResult?.tags].some(isPendingLine);
  const isPolling = isAnyLinePending && attempt < MAX_POLL_ATTEMPTS;

  useEffect(() => {
    getOnboardingResult({ updateState, clientId, state });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  useEffect(() => {
    if (!isPolling) return undefined;

    const timeoutId = setTimeout(() => setAttempt(attempt + 1), POLL_INTERVAL);
    return () => clearTimeout(timeoutId);
  }, [isPolling, attempt, onboardingResult]);

  if (error) return null;

  if (!onboardingResult) {
    return (
      <Grid container item justifyContent="center" sx={{ padding: '60px' }}>
        <CircularProgress color="lightDark" data-testid="result_loader" />
      </Grid>
    );
  }

  const {
    connections = [],
    webhook,
    tags,
    instructions = [],
    returnUri,
    guide,
    finish,
    layout,
  } = onboardingResult;
  const webhookLine = webhook ? getWebhookLine(webhook, isPolling) : null;
  const tagsLine = tags ? getTagsLine(tags, isPolling) : null;

  if (layout === 'summary') {
    return (
      <SummaryResult
        result={onboardingResult}
        isPolling={isPolling}
        failedLogos={failedLogos}
        onLogoFailed={(url) => setFailedLogos((previous) => [...previous, url])}
      />
    );
  }

  return (
    <Grid container item justifyContent="center" direction="column" sx={{ margin: '0 auto' }}>
      <ConnectedLogos
        connections={connections}
        failed={failedLogos}
        onFailed={(url) => setFailedLogos((previous) => [...previous, url])}
      />

      <Grid item xs sx={{ paddingBottom: '24px' }}>
        <Typography
          gutterBottom
          data-testid="result_title"
          sx={{ textAlign: 'center', color: '#484848', fontSize: '18px', fontWeight: 700 }}
        >
          <FormattedMessage id="result_title" />
        </Typography>
      </Grid>

      <List data-testid="result_statuses">
        {connections.map((connection) => {
          const isConnected = connection.status === RESULT_STATUSES.CONNECTED;

          return (
            <ListItem key={connection.connectionId} divider>
              <ListItemIcon sx={{ minWidth: '36px' }}>
                {STATUS_ICONS[isConnected ? 'ok' : 'failed']}
              </ListItemIcon>
              <ListItemText
                primary={
                  <FormattedMessage
                    id={isConnected ? 'result_connected' : 'result_not_connected'}
                    values={{ appName: connection.title }}
                  />
                }
              />
            </ListItem>
          );
        })}

        {webhookLine ? (
          <ListItem divider>
            <ListItemIcon sx={{ minWidth: '36px' }}>{STATUS_ICONS[webhookLine.icon]}</ListItemIcon>
            <ListItemText primary={<FormattedMessage id={webhookLine.messageId} />} />
          </ListItem>
        ) : null}

        {tagsLine ? (
          <ListItem divider data-testid="result_tags">
            <ListItemIcon sx={{ minWidth: '36px' }}>{STATUS_ICONS[tagsLine.icon]}</ListItemIcon>
            <ListItemText primary={<FormattedMessage id={tagsLine.messageId} />} />
          </ListItem>
        ) : null}
      </List>

      {instructions.length ? (
        <Grid item sx={{ paddingTop: '30px' }} data-testid="result_instructions">
          <Typography gutterBottom sx={{ fontWeight: 700 }}>
            <FormattedMessage id="result_instructions_title" />
          </Typography>
          <ol style={{ margin: 0, paddingLeft: '20px' }}>
            {instructions.map((instruction) => (
              <li key={instruction}>
                <Typography variant="body2" sx={{ padding: '4px 0' }}>
                  <FormattedMessage id={instruction} defaultMessage={instruction} />
                </Typography>
              </li>
            ))}
          </ol>
        </Grid>
      ) : null}

      {guide?.url && guide?.label ? (
        <Grid item sx={{ paddingTop: '12px' }}>
          <Button
            variant="text"
            href={localizedUrl(intl.messages, guide.label, guide.url)}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<DescriptionOutlinedIcon />}
            endIcon={<OpenInNewRoundedIcon sx={{ fontSize: '16px !important' }} />}
            sx={{ textTransform: 'none', fontSize: '13px' }}
            fullWidth
            data-testid="result_guide"
          >
            <FormattedMessage id={guide.label} defaultMessage={guide.label} />
          </Button>
        </Grid>
      ) : null}

      <FinishButton finish={finish} returnUri={returnUri} />
    </Grid>
  );
};
